/** @jsx jsx */
import React, { type ReactNode } from 'react';
import { jsx, css } from '@compiled/react';
import { token } from '@atlaskit/tokens';

/**
 * ThemeTokensReset resets all --project-* CSS variables back to their
 * default Atlaskit values (BASE_THEME).
 *
 * Use this component to wrap content that should NOT be themed, even when
 * rendered within a themed route. This is useful for "inset" layouts where
 * the main content area has a white/default background.
 *
 * Example:
 * ```tsx
 * // Themed route with inset container
 * <ThemeTokensSetter themeSetting={theme} />
 * <ThemedButton>This uses project theme colors</ThemedButton>
 *
 * <ThemeTokensReset>
 *   <ThemedButton>This uses default Atlaskit colors</ThemedButton>
 * </ThemeTokensReset>
 * ```
 *
 * This eliminates the need for `isThemed ? ThemedButton : Button` checks
 * throughout the codebase. Components can always use ThemedButton, and
 * the styling will be determined by CSS variable inheritance.
 *
 * Note: The component uses flexbox layout to fill available space within
 * its parent container. Children should be flex items or contain their own
 * layout logic as needed.
 */
export const ThemeTokensReset = ({ children }: { children: ReactNode }): JSX.Element => (
	<div css={resetStyles}>{children}</div>
);

/**
 * These styles reset all --project-* CSS variables back to their default
 * Atlaskit token values. When ThemedButton reads these variables, it will
 * get the standard Atlaskit styling instead of the project theme.
 *
 * Layout properties ensure the component fills available space and doesn't
 * collapse when used in flex containers.
 *
 * Note: These token values are inlined directly (not imported from BASE_THEME)
 * to comply with Compiled's requirement that styles cannot be imported from
 * other files.
 *
 * IMPORTANT: If changing this file, make sure to update the BASE_THEME constant in the project-theme-tokens package.
 */
const resetStyles = css({
	// CSS variable resets - inlined from BASE_THEME
	'--project-color-background-neutral-subtle-hovered-contrast': token(
		'color.background.neutral.subtle.hovered',
	),
	'--project-color-background-accent-selected': '#8FB8F6',
	'--project-color-background-brand-bold': token('color.background.brand.bold'),
	'--project-color-background-brand-bold-hovered': token('color.background.brand.bold.hovered'),
	'--project-color-background-brand-bold-pressed': token('color.background.brand.bold.pressed'),
	'--project-color-background-disabled': token('color.background.disabled'),
	'--project-background-input': token('color.background.input'),
	'--project-background-neutral': token('color.background.neutral'),
	'--project-background-neutral-hovered': token('color.background.neutral.hovered'),
	'--project-background-neutral-pressed': token('color.background.neutral.pressed'),
	'--project-background-neutral-subtle-hovered': token('color.background.neutral.subtle.hovered'),
	'--project-background-neutral-subtle-pressed': token('color.background.neutral.subtle.pressed'),
	'--project-background-selected': token('color.background.selected'),
	'--project-color-border': token('color.border'),
	'--project-color-border-input': token('color.border.input'),
	'--project-color-border-inverse': token('color.border.inverse'),
	'--project-color-border-selected': token('color.border.selected'),
	'--project-color-elevation-surface': token('elevation.surface'),
	'--project-color-elevation-surface-sunken': token('elevation.surface.sunken'),
	'--project-color-icon-danger': token('color.icon.danger'),
	'--project-color-icon-information': token('color.icon.information'),
	'--project-color-icon-success': token('color.icon.success'),
	'--project-color-icon-warning': token('color.icon.warning'),
	'--project-color-link': token('color.link'),
	'--project-color-link-pressed': token('color.link.pressed'),
	'--project-skeleton': token('color.background.neutral'),
	'--project-color-text': token('color.text'),
	'--project-text-accent-selected': token('color.text.warning.inverse'),
	'--project-color-text-disabled': token('color.text.disabled'),
	'--project-color-text-inverse': token('color.text.inverse'),
	'--project-color-text-selected': token('color.text.selected'),
	'--project-color-text-subtle': token('color.text.subtle'),
	'--project-color-text-subtlest': token('color.text.subtlest'),
	'--project-elevation-surface-swimlane': token('elevation.surface'),
	// Layout properties to fill available space
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
	minHeight: 0,
});
